import { useEffect, useState } from "react";
import { ThemeSettingModel } from "states/theme-setting/theme-setting.models";
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
} from "antd";
import { Link, useHistory, useParams } from "react-router-dom";
import { RollbackOutlined, SaveOutlined } from "@ant-design/icons";
import _ from "lodash";
import { t, CoreResource } from "shared/plugins/localization";
import {
  TheFieldIsRequired,
  Theme,
  ThemeManagerText,
} from "shared/constants/define-localization";
import {
  ThemeTemplateItem,
  ThemeTemplateModel,
} from "states/theme-template/theme-template.models";
import { getThemeTemplateList } from "states/theme-template/theme-template.api";
import {
  convertToThemeTemplateItem,
  setValueFromTemplate,
} from "states/theme-setting/theme-setting.services";
import {
  addThemeSettingApi,
  getThemeSettingByIdApi,
  updateThemeSettingApi,
} from "states/theme-setting/theme-setting.api";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setLoading } from "states/global-value/global-value.slice";
import {
  ADD_THEME_SETTING_ROUTE,
  THEME_SETTING_ROUTE,
} from "shared/constants/routes";
import { ThemeSettingGroup } from "shared/components/custom-theme/theme-setting-group";
import { RootState } from "app.store";
import { LanguageSelector } from "shared/components/language-selector";

type TParams = { id: string };

const AddUpdateThemeSetting = () => {
  const { id } = useParams<TParams>();
  const dispatch = useDispatch();
  const clientLanguages = useSelector(
    (state: RootState) => state.localization.clientLanguages,
    shallowEqual
  );
  const [currentLanguage, setCurrentLanguage] = useState<string>();
  const history = useHistory();
  const [model, setModel] = useState<ThemeSettingModel>({
    value: {},
  } as ThemeSettingModel);
  const [themeTemplateList, setThemeTemplateList] = useState<
    ThemeTemplateModel[]
  >([]);
  const [currentTemplate, setCurrentTemplate] = useState<ThemeTemplateItem>();
  const [action, setAction] = useState<string>("");

  const _handleGetUpdate = async () => {
    try {
      const response = await getThemeSettingByIdApi(id);
      setModel(response.data);
    } catch (error) {
      message.error(error?.response?.data?.error?.message ?? "Lỗi hệ thống");
    }
  };
  const onSaved = async () => {
    if (model.name === null || model.name === "") {
      message.error(t(CoreResource, TheFieldIsRequired, "Tên"));
      return false;
    }
    if (model.slug === null || model.slug === "") {
      message.error(
        t(CoreResource, TheFieldIsRequired, "key", t(CoreResource, Theme))
      );
      return false;
    }
    dispatch(setLoading(true));
    try {
      if (action === "add") {
        await addThemeSettingApi(model, currentLanguage);
      } else {
        await updateThemeSettingApi(model, currentLanguage);
      }
      message.success("Cập nhật thành công");
      history.go(0);
    } catch (error) {
      message.error(error?.response?.data?.error?.message);
    }
    dispatch(setLoading(false));
  };

  const _handleChangeTemplate = (templateId: string) => {
    model.themeTemplateId = templateId;
    const template = themeTemplateList.find((m) => m.id === templateId);
    if (!template) return;
    const templateItem = convertToThemeTemplateItem(template);
    model.value = setValueFromTemplate(templateItem);
    setModel({ ...model });
  };

  const _handleOnChangeModelValue = (newModel: any) => {
    setModel({ ...model, value: newModel });
  };

  useEffect(() => {
    getThemeTemplateList().then((res) => {
      setThemeTemplateList(res.data);
    });
    if (document.location.href.indexOf(ADD_THEME_SETTING_ROUTE) > 0) {
      setAction("add");
    } else {
      setAction("edit");
      _handleGetUpdate();
    }
    document.title = t(CoreResource, ThemeManagerText);
  }, []);

  useEffect(() => {
    if (themeTemplateList.length > 0 && model.themeTemplateId) {
      const template = themeTemplateList.find(
        (m) => m.id === model.themeTemplateId
      );
      if (template) {
        const templateItem = convertToThemeTemplateItem(template);
        setCurrentTemplate(templateItem);
      }
    }
  }, [themeTemplateList, model.themeTemplateId]);

  useEffect(() => {
    if (clientLanguages.length > 0 && !currentLanguage) {
      setCurrentLanguage(clientLanguages.find((m) => m.isDefault)?.langCode);
    }
  }, [clientLanguages]);

  return (
    <div className="theme-config-update">
      <Row gutter={[16, 16]} className="mb-4">
        <Col flex="auto">
          {" "}
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={THEME_SETTING_ROUTE}>Quản lí giao diện</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {action === "add" ? "Thêm " : "Cập nhật "} giao diện
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col flex="auto" className="d-flex justify-content-end">
          <Space>
            {action === "edit" && (
              <LanguageSelector
                onChange={async (value) => {
                  const res = await getThemeSettingByIdApi(
                    model.id ?? "",
                    value
                  );
                  if (!res.data.value) {
                    Modal.confirm({
                      title: "Thông báo",
                      content:
                        "Nội dung chưa được khởi tạo cho ngôn ngữ này, bạn có muốn khởi tạo?",
                      onOk: () => {
                        setCurrentLanguage(value);
                      },
                    });
                    return;
                  } else {
                    setCurrentLanguage(value);
                    setModel(res.data);
                  }
                }}
                selectedCode={currentLanguage}
              />
            )}
            <Link to={THEME_SETTING_ROUTE} className="mr-2">
              <Button type="default" icon={<RollbackOutlined />}>
                Quay lại
              </Button>
            </Link>
            <Button onClick={onSaved} type="primary" icon={<SaveOutlined />}>
              Lưu
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb-4">
        <Col span={8}>
          <Input
            value={model.name}
            placeholder="Tên giao diện"
            onChange={(ev) => {
              setModel({ ...model, name: ev.target.value });
            }}
          />
        </Col>
        <Col span={8}>
          <Input
            value={model.slug}
            placeholder="THEME_NAME KEY (*)"
            onChange={(ev) => {
              setModel({ ...model, slug: ev.target.value });
            }}
          />
        </Col>
        <Col span={8}>
          <Select
            className="w-100"
            placeholder="Chọn mẫu"
            value={model.themeTemplateId}
            onChange={_handleChangeTemplate}
            showSearch
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {themeTemplateList.map((template) => (
              <Select.Option value={template.id ? template.id : ""}>
                {template.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      {currentTemplate && (
        <ThemeSettingGroup
          model={model.value}
          onChange={_handleOnChangeModelValue}
          itemKey={"1"}
          template={currentTemplate}
        />
      )}
    </div>
  );
};

export default AddUpdateThemeSetting;
